import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Card, Table, Form, Button, Modal } from 'react-bootstrap';
import CreatableSelect from 'react-select/creatable';
import darkSelectStyles from '../utils/darkSelectStyles';
import { formatDay } from '../utils/formatDate';

const RANGE_OPTIONS = {
  month: { label: 'Este Mes', days: null },
  '90d': { label: 'Últimos 90 Días', days: 90 },
  all: { label: 'Todo', days: null },
};

const INACTIVE_DAYS = 7;
const INACTIVE_PAGE_SIZE = 10;

const OperationsInsightsPanel = () => {
  const [dispatches, setDispatches] = useState([]);
  const [fillingOrders, setFillingOrders] = useState([]);
  const [range, setRange] = useState('month');

  // --- Filling order (add / reorder / remove) ---
  const [clientsList, setClientsList] = useState([]);
  const [newClient, setNewClient] = useState(null);
  const [newQuantity, setNewQuantity] = useState('');
  const [orderError, setOrderError] = useState('');
  const [savingOrder, setSavingOrder] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [removeIndex, setRemoveIndex] = useState(null);

  const refreshFillingOrder = () => {
    fetch('/api/filling-order').then((res) => res.json()).then((data) => setFillingOrders(Array.isArray(data) ? data : []))
      .catch((err) => console.error('❌ Error fetching filling orders:', err));
  };

  const refreshDispatches = () => {
    fetch('/api/dispatches').then((res) => res.json()).then((data) => setDispatches(Array.isArray(data) ? data : []))
      .catch((err) => console.error('❌ Error fetching dispatches:', err));
  };

  const refreshClientsList = () => {
    fetch('/api/clients').then((res) => res.json()).then((data) => setClientsList(Array.isArray(data) ? data : []))
      .catch((err) => console.error('❌ Error fetching clients:', err));
  };

  // Kept in sync with the state of the same name so the polling interval
  // below (fixed at mount) can read the *current* value instead of a stale
  // closure — skips refreshing out from under an in-flight save or drag.
  const savingOrderRef = useRef(false);
  useEffect(() => { savingOrderRef.current = savingOrder; }, [savingOrder]);
  const dragIndexRef = useRef(null);
  useEffect(() => { dragIndexRef.current = dragIndex; }, [dragIndex]);

  useEffect(() => {
    refreshDispatches();
    refreshClientsList();
    refreshFillingOrder();
  }, []);

  // Keeps the order card live — e.g. a filler marking something complete, or
  // another admin editing the queue — without requiring a manual reload.
  // Also re-pulls dispatches/clients so the "Despachados" and "Clientes
  // Inactivos" cards (both derived from those two lists) pick up a client
  // merge or rename made elsewhere without needing a full page reload —
  // neither of those two lists is tied to the drag/save guard below, since
  // that guard only protects the filling-order reorder UI.
  useEffect(() => {
    const interval = setInterval(() => {
      refreshDispatches();
      refreshClientsList();
      if (savingOrderRef.current || dragIndexRef.current !== null) return;
      refreshFillingOrder();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const pendingOrder = useMemo(
    () => fillingOrders.filter((o) => !o.completed).sort((a, b) => a.position - b.position),
    [fillingOrders]
  );
  const pendingTotal = useMemo(() => pendingOrder.reduce((sum, o) => sum + (Number(o.quantity) || 0), 0), [pendingOrder]);

  // Persists a full replacement order (pending, in the given order, plus the
  // untouched completed entries) — the backend wipes and re-inserts the
  // whole collection on every save, so completed history has to be resent
  // or it's lost. Re-fetches completed entries right before saving (instead
  // of trusting this component's possibly-stale state) so a filler marking
  // something done in between can't get silently reverted to pending by an
  // admin action based on an older snapshot.
  const persistPendingOrder = async (nextPending) => {
    setOrderError('');
    setSavingOrder(true);

    try {
      const freshRes = await fetch('/api/filling-order');
      const fresh = await freshRes.json();
      const freshCompleted = (Array.isArray(fresh) ? fresh : []).filter((o) => o.completed);
      const freshCompletedClients = new Set(freshCompleted.map((o) => o.client));
      const safeNextPending = nextPending.filter((p) => !freshCompletedClients.has(p.client));

      const fullOrder = [...safeNextPending, ...freshCompleted].map((entry, idx) => ({
        client: entry.client,
        quantity: Number(entry.quantity) || 0,
        position: idx,
        completed: !!entry.completed,
        notes: entry.notes || [],
      }));

      const res = await fetch('/api/filling-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fullOrder),
      });
      if (!res.ok) throw new Error('No se pudo guardar el orden de llenado.');
      refreshFillingOrder();
    } catch (err) {
      console.error('❌ Error saving filling order:', err);
      setOrderError('No se pudo guardar el orden de llenado. Intente de nuevo.');
    } finally {
      setSavingOrder(false);
    }
  };

  const handleAddClient = () => {
    const clientName = (newClient?.value || '').trim();
    const quantity = Number(newQuantity);

    if (!clientName) return setOrderError('Seleccione o escriba un cliente.');
    if (!quantity || quantity <= 0) return setOrderError('Ingrese una cantidad mayor a 0.');
    if (pendingOrder.some((o) => o.client.toLowerCase() === clientName.toLowerCase())) {
      return setOrderError(`"${clientName}" ya está en la lista — edite su cantidad en la tabla en lugar de agregarlo de nuevo.`);
    }

    persistPendingOrder([...pendingOrder, { client: clientName, quantity, completed: false }]);
    setNewClient(null);
    setNewQuantity('');
  };

  const handleCreateClient = async (value) => {
    try {
      const res = await fetch('/api/clients', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: value }),
      });
      const data = await res.json();
      if (!res.ok) {
        setOrderError(data.error || 'Error al guardar el cliente.');
        return;
      }
      setClientsList((prev) => [...prev, value]);
      setNewClient({ label: value, value });
    } catch (err) {
      console.error('❌ Error creating client:', err);
      setOrderError('Error de conexión al guardar el cliente.');
    }
  };

  const handleRemoveEntry = (index) => {
    setRemoveIndex(index);
  };

  const cancelRemove = () => setRemoveIndex(null);

  const confirmRemove = () => {
    persistPendingOrder(pendingOrder.filter((_, i) => i !== removeIndex));
    setRemoveIndex(null);
  };

  const handleQuantityBlur = (index, value) => {
    const quantity = Number(value);
    if (!quantity || quantity <= 0 || quantity === pendingOrder[index].quantity) return;
    const next = pendingOrder.map((o, i) => (i === index ? { ...o, quantity } : o));
    persistPendingOrder(next);
  };

  const handleDrop = (index) => {
    if (dragIndex === null || dragIndex === index) {
      setDragIndex(null);
      setDragOverIndex(null);
      return;
    }
    const next = [...pendingOrder];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(index, 0, moved);
    setDragIndex(null);
    setDragOverIndex(null);
    persistPendingOrder(next);
  };

  // Most recent despacho per client — specifically the dispatch side (not
  // contributions), since "last order" means the last time we shipped to
  // them, not the last time they dropped off tanks.
  const clientLastDispatch = useMemo(() => {
    const map = {};
    dispatches.forEach((dispatch) => {
      const t = new Date(dispatch.timestamp).getTime();
      (dispatch.contributions || []).forEach((c) => {
        const client = c.destinationClient || c.client;
        if (!client) return;
        if (!map[client] || t > map[client].timestamp) {
          map[client] = { timestamp: t, amount: c.amount || 0 };
        }
      });
    });
    return map;
  }, [dispatches]);

  // Only clients with at least one despacho on record, whose most recent one
  // is older than the cutoff — a client that has never been dispatched to
  // isn't "inactive", it just has no order history yet, and including it
  // here would flag every newly-added client as a false positive.
  const inactiveClients = useMemo(() => {
    const cutoff = Date.now() - INACTIVE_DAYS * 24 * 60 * 60 * 1000;
    return clientsList
      .filter((name) => {
        const last = clientLastDispatch[name];
        return last && last.timestamp < cutoff;
      })
      .sort();
  }, [clientsList, clientLastDispatch]);

  // Defaults to oldest/never-ordered first (the ones most worth a follow-up
  // call), but any column header can override it.
  const [inactiveSortConfig, setInactiveSortConfig] = useState({ key: 'lastTimestamp', direction: 'asc' });

  const handleInactiveSort = (key) => {
    setInactiveSortConfig((prev) =>
      prev.key === key ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' } : { key, direction: 'asc' }
    );
    setInactivePage(1);
  };

  const inactiveClientRows = useMemo(() => {
    const rows = inactiveClients.map((name) => {
      const last = clientLastDispatch[name];
      return { client: name, lastTimestamp: last ? last.timestamp : null, lastAmount: last ? last.amount : null };
    });

    const { key, direction } = inactiveSortConfig;
    const dir = direction === 'asc' ? 1 : -1;
    return rows.sort((a, b) => {
      if (key === 'client') return a.client.localeCompare(b.client) * dir;
      if (key === 'lastAmount') return ((a.lastAmount || 0) - (b.lastAmount || 0)) * dir;
      return ((a.lastTimestamp || 0) - (b.lastTimestamp || 0)) * dir;
    });
  }, [inactiveClients, clientLastDispatch, inactiveSortConfig]);

  const [inactivePage, setInactivePage] = useState(1);
  const inactiveTotalPages = Math.max(1, Math.ceil(inactiveClientRows.length / INACTIVE_PAGE_SIZE));
  const inactiveCurrentPage = Math.min(inactivePage, inactiveTotalPages);
  const inactivePageRows = inactiveClientRows.slice(
    (inactiveCurrentPage - 1) * INACTIVE_PAGE_SIZE,
    inactiveCurrentPage * INACTIVE_PAGE_SIZE
  );

  // Boundaries for the selected period and the equivalent period right
  // before it, so the card can show "current vs previous" regardless of
  // which range is picked — 'all' has no meaningful "previous" window.
  const periodBounds = useMemo(() => {
    const now = new Date();
    if (range === 'month') {
      const currentStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const previousStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return { currentStart, previousStart, previousEnd: currentStart };
    }
    if (range === '90d') {
      const currentStart = new Date(now);
      currentStart.setDate(currentStart.getDate() - 90);
      const previousStart = new Date(currentStart);
      previousStart.setDate(previousStart.getDate() - 90);
      return { currentStart, previousStart, previousEnd: currentStart };
    }
    return { currentStart: null, previousStart: null, previousEnd: null }; // 'all'
  }, [range]);

  const dispatchComparison = useMemo(() => {
    const { currentStart, previousStart, previousEnd } = periodBounds;
    let currentTotal = 0;
    let previousTotal = 0;

    dispatches.forEach((dispatch) => {
      const t = new Date(dispatch.timestamp);
      const amt = dispatch.cantidad || 0;
      if (!currentStart || t >= currentStart) {
        currentTotal += amt;
      } else if (previousStart && t >= previousStart && t < previousEnd) {
        previousTotal += amt;
      }
    });

    return { currentTotal, previousTotal, diff: currentTotal - previousTotal, hasPrevious: range !== 'all' };
  }, [dispatches, periodBounds, range]);

  const dispatchByClient = useMemo(() => {
    const { currentStart, previousStart, previousEnd } = periodBounds;
    const byClient = {};

    dispatches.forEach((dispatch) => {
      const t = new Date(dispatch.timestamp);
      const inCurrent = !currentStart || t >= currentStart;
      const inPrevious = previousStart && t >= previousStart && t < previousEnd;
      if (!inCurrent && !inPrevious) return;

      (dispatch.contributions || []).forEach((c) => {
        const client = c.destinationClient || c.client || 'Sin cliente';
        byClient[client] = byClient[client] || { current: 0, previous: 0 };
        if (inCurrent) byClient[client].current += c.amount || 0;
        else byClient[client].previous += c.amount || 0;
      });
    });

    return Object.entries(byClient)
      .map(([client, v]) => ({ client, current: v.current, previous: v.previous, diff: v.current - v.previous }));
  }, [dispatches, periodBounds]);

  // Clicking a column header sorts by it; clicking it again flips the
  // direction. Numbers start descending (biggest first), names ascending.
  const [dispatchSort, setDispatchSort] = useState({ key: 'current', dir: 'desc' });
  const toggleDispatchSort = (key) => {
    setDispatchSort((prev) => (prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: key === 'client' ? 'asc' : 'desc' }));
  };
  const sortedDispatchByClient = useMemo(() => {
    const { key, dir } = dispatchSort;
    const sign = dir === 'asc' ? 1 : -1;
    return [...dispatchByClient].sort((a, b) => {
      const cmp = key === 'client'
        ? a.client.localeCompare(b.client, 'es', { sensitivity: 'base' })
        : a[key] - b[key];
      return cmp * sign || a.client.localeCompare(b.client, 'es', { sensitivity: 'base' });
    });
  }, [dispatchByClient, dispatchSort]);
  const renderDispatchSortHeader = (key, label) => {
    const active = dispatchSort.key === key;
    return (
      <th
        onClick={() => toggleDispatchSort(key)}
        style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', position: 'sticky', top: 0, background: 'var(--mm-panel)', zIndex: 1 }}
        title="Ordenar"
      >
        {label}
        <i
          className={`bi bi-${active ? (dispatchSort.dir === 'asc' ? 'caret-up-fill' : 'caret-down-fill') : 'chevron-expand'} ml-1`}
          style={{ fontSize: '0.7rem', opacity: active ? 1 : 0.4 }}
        />
      </th>
    );
  };

  return (
    <>
    <div className="row mb-4">
      <div className="col-lg-6 mb-4">
        <Card className="h-100">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Card.Title className="mb-0">Clientes Inactivos</Card.Title>
              <span className="text-muted" style={{ fontSize: '0.8rem' }}>{INACTIVE_DAYS}+ días sin actividad</span>
            </div>
            <div
              style={{ fontSize: '2.5rem', fontWeight: 'bold', lineHeight: 1.1 }}
              className={inactiveClients.length > 0 ? 'text-mm-yellow' : undefined}
            >
              {inactiveClients.length}
            </div>
            <div className="text-muted mb-2" style={{ fontSize: '0.85rem' }}>
              de {clientsList.length} cliente{clientsList.length === 1 ? '' : 's'}
            </div>
            {inactiveClientRows.length > 0 ? (
              <>
                <div className="table-responsive">
                  <Table size="sm" className="align-middle mb-0">
                    <thead>
                      <tr>
                        <th style={{ cursor: 'pointer' }} onClick={() => handleInactiveSort('client')}>
                          Cliente
                          {inactiveSortConfig.key === 'client' && (
                            <i className={`bi bi-caret-${inactiveSortConfig.direction === 'asc' ? 'up' : 'down'}-fill ml-1`} />
                          )}
                        </th>
                        <th style={{ cursor: 'pointer' }} onClick={() => handleInactiveSort('lastTimestamp')}>
                          Última Orden
                          {inactiveSortConfig.key === 'lastTimestamp' && (
                            <i className={`bi bi-caret-${inactiveSortConfig.direction === 'asc' ? 'up' : 'down'}-fill ml-1`} />
                          )}
                        </th>
                        <th style={{ cursor: 'pointer' }} onClick={() => handleInactiveSort('lastAmount')}>
                          Cantidad
                          {inactiveSortConfig.key === 'lastAmount' && (
                            <i className={`bi bi-caret-${inactiveSortConfig.direction === 'asc' ? 'up' : 'down'}-fill ml-1`} />
                          )}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {inactivePageRows.map((r) => (
                        <tr key={r.client}>
                          <td>{r.client}</td>
                          <td>{formatDay(r.lastTimestamp, 'Sin despachos')}</td>
                          <td>{r.lastAmount !== null ? r.lastAmount : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                {inactiveTotalPages > 1 && (
                  <div className="d-flex justify-content-between align-items-center mt-2">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={inactiveCurrentPage === 1}
                      onClick={() => setInactivePage((p) => Math.max(1, p - 1))}
                    >
                      Anterior
                    </Button>
                    <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                      Página {inactiveCurrentPage} de {inactiveTotalPages}
                    </span>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={inactiveCurrentPage === inactiveTotalPages}
                      onClick={() => setInactivePage((p) => Math.min(inactiveTotalPages, p + 1))}
                    >
                      Siguiente
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-muted">Todos los clientes tienen actividad reciente.</div>
            )}
          </Card.Body>
        </Card>
      </div>

      <div className="col-lg-6 mb-4">
        <Card className="h-100">
          <Card.Body className="d-flex flex-column">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <Card.Title className="mb-0">Despachados</Card.Title>
              <Form.Select size="sm" style={{ width: '150px' }} value={range} onChange={(e) => setRange(e.target.value)}>
                {Object.entries(RANGE_OPTIONS).map(([key, opt]) => (
                  <option key={key} value={key}>{opt.label}</option>
                ))}
              </Form.Select>
            </div>
            <div style={{ fontSize: '2.5rem', fontWeight: 'bold', lineHeight: 1.1 }}>
              {dispatchComparison.currentTotal}
            </div>
            <div className="text-muted mb-2" style={{ fontSize: '0.85rem' }}>
              cilindros despachados · {RANGE_OPTIONS[range].label}
            </div>
            {dispatchComparison.hasPrevious && (
              <div className={dispatchComparison.diff >= 0 ? 'text-mm-green' : 'text-mm-red'}>
                <i className={`bi bi-arrow-${dispatchComparison.diff >= 0 ? 'up' : 'down'}-short me-1`} />
                {Math.abs(dispatchComparison.diff)} {dispatchComparison.diff >= 0 ? 'más' : 'menos'} que el período anterior ({dispatchComparison.previousTotal})
              </div>
            )}
            {/* Fills whatever height the card has left (the row is as tall as
                the neighbouring card), scrolling only past that. The absolute
                inner box keeps the table's own length from stretching the card. */}
            <div className="mt-3" style={{ position: 'relative', flex: '1 1 auto', minHeight: '220px' }}>
            <div className="table-responsive" style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, overflowY: 'auto' }}>
              <Table size="sm" className="align-middle mb-0">
                <thead>
                  <tr>
                    {renderDispatchSortHeader('client', 'Cliente')}
                    {renderDispatchSortHeader('current', 'Despachado')}
                    {dispatchComparison.hasPrevious && renderDispatchSortHeader('previous', 'Anterior')}
                    {dispatchComparison.hasPrevious && renderDispatchSortHeader('diff', 'Dif.')}
                  </tr>
                </thead>
                <tbody>
                  {sortedDispatchByClient.length > 0 ? sortedDispatchByClient.map((r) => (
                    <tr key={r.client}>
                      <td>{r.client}</td>
                      <td>{r.current}</td>
                      {dispatchComparison.hasPrevious && <td>{r.previous}</td>}
                      {dispatchComparison.hasPrevious && (
                        <td className={r.diff < 0 ? 'text-mm-red' : 'text-mm-green'}>
                          {r.diff > 0 ? '+' : ''}{r.diff}
                        </td>
                      )}
                    </tr>
                  )) : (
                    <tr><td colSpan={dispatchComparison.hasPrevious ? 4 : 2} className="text-muted text-center">Sin datos para este período.</td></tr>
                  )}
                </tbody>
              </Table>
            </div>
            </div>
          </Card.Body>
        </Card>
      </div>

    </div>

    <div className="row mb-4">
      <div className="col-12">
        <Card className="mb-4">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <Card.Title className="mb-0">Orden de Llenado</Card.Title>
              <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                {savingOrder ? (
                  <span><i className="bi bi-arrow-repeat me-1" />Guardando…</span>
                ) : (
                  `${pendingOrder.length} cliente${pendingOrder.length === 1 ? '' : 's'} pendiente${pendingOrder.length === 1 ? '' : 's'} · ${pendingTotal} cilindros`
                )}
              </div>
            </div>

            <div className="row g-2 align-items-end mb-3">
              <div className="col-md-6">
                <Form.Label className="small mb-1">Cliente</Form.Label>
                <CreatableSelect
                  isClearable
                  styles={darkSelectStyles}
                  value={newClient}
                  onChange={setNewClient}
                  onCreateOption={handleCreateClient}
                  options={clientsList.map((c) => ({ label: c, value: c }))}
                  placeholder="Buscar o crear cliente..."
                />
              </div>
              <div className="col-md-3">
                <Form.Label className="small mb-1">Cantidad</Form.Label>
                <Form.Control
                  type="number"
                  min="1"
                  value={newQuantity}
                  onChange={(e) => setNewQuantity(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddClient()}
                  placeholder="Cilindros"
                />
              </div>
              <div className="col-md-3">
                <Button variant="success" className="w-100" onClick={handleAddClient} disabled={savingOrder}>
                  <i className="bi bi-plus-lg me-1" /> Agregar
                </Button>
              </div>
            </div>

            {orderError && <div className="text-mm-red mb-2" style={{ fontSize: '0.85rem' }}>{orderError}</div>}

            <div className="table-responsive">
              <Table size="sm" className="align-middle mb-0">
                <thead>
                  <tr>
                    <th style={{ width: '32px' }}></th>
                    <th style={{ width: '32px' }}>#</th>
                    <th>Cliente</th>
                    <th style={{ width: '140px' }}>Cantidad</th>
                    <th style={{ width: '48px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {pendingOrder.length > 0 ? pendingOrder.map((entry, index) => (
                    <tr
                      key={entry._id || entry.client}
                      draggable={!savingOrder}
                      onDragStart={() => setDragIndex(index)}
                      onDragOver={(e) => { e.preventDefault(); setDragOverIndex(index); }}
                      onDrop={() => handleDrop(index)}
                      onDragEnd={() => { setDragIndex(null); setDragOverIndex(null); }}
                      style={{
                        cursor: savingOrder ? 'default' : 'grab',
                        opacity: dragIndex === index ? 0.4 : 1,
                        borderTop: dragOverIndex === index && dragIndex !== index ? '2px solid var(--mm-cyan)' : undefined,
                      }}
                    >
                      <td className="text-muted"><i className="bi bi-grip-vertical" /></td>
                      <td className="text-muted">{index + 1}</td>
                      <td>{entry.client}</td>
                      <td>
                        <Form.Control
                          type="number"
                          min="1"
                          size="sm"
                          defaultValue={entry.quantity}
                          key={`${entry._id}-${entry.quantity}`}
                          onBlur={(e) => handleQuantityBlur(index, e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && e.target.blur()}
                        />
                      </td>
                      <td>
                        <Button variant="link" className="text-mm-red p-0" onClick={() => handleRemoveEntry(index)} title="Quitar de la lista">
                          <i className="bi bi-trash" />
                        </Button>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan="5" className="text-muted text-center py-3">No hay clientes pendientes de llenado.</td></tr>
                  )}
                </tbody>
              </Table>
            </div>
            <div className="text-muted mt-2" style={{ fontSize: '0.78rem' }}>
              <i className="bi bi-info-circle me-1" />
              Arrastre una fila para cambiar el orden. Los cambios se guardan automáticamente y el llenador deberá confirmarlos.
            </div>
          </Card.Body>
        </Card>
      </div>
    </div>

    <Modal show={removeIndex !== null} onHide={cancelRemove} centered>
      <Modal.Header closeButton>
        <Modal.Title>Quitar del Orden de Llenado</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {removeIndex !== null && (
          <>¿Quitar a <strong>"{pendingOrder[removeIndex]?.client}"</strong> del orden de llenado?</>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={cancelRemove}>Cancelar</Button>
        <Button variant="danger" onClick={confirmRemove}>Quitar</Button>
      </Modal.Footer>
    </Modal>
    </>
  );
};

export default OperationsInsightsPanel;
